import api from './api';
import axios from 'axios';
import type {
  ComplianceSummaryResponseContract,
  ProcuracaoComplianceContract,
} from '@sstexames/shared-types';

class ComplianceService {
  /**
   * Obtém o resumo consolidado de Compliance (Procuração, Contrato e Laudos)
   */
  async getSummary(): Promise<ComplianceSummaryResponseContract> {
    const response = await api.get<ComplianceSummaryResponseContract>('/compliance');
    return response.data;
  }

  /**
   * Faz upload de uma nova procuração eletrônica com renovação de vigência.
   * Utiliza Presigned URL (upload direto para GCS / CDN) sem sobrecarregar a memória da API.
   */
  async uploadProcuracao(
    file: File,
    vigenciaMeses: number = 24,
    onProgress?: (percent: number) => void,
  ): Promise<ProcuracaoComplianceContract> {
    // 1. Solicita Presigned URL da API
    const presignedRes = await api.post<{
      uploadUrl: string;
      finalUrl: string;
      isDirectGcs: boolean;
    }>('/compliance/presigned-url', {
      nome_arquivo: file.name,
      mimetype: file.type || 'application/pdf',
    });

    const { uploadUrl, finalUrl, isDirectGcs } = presignedRes.data;

    // 2. Stream direto do browser para o Google Cloud Storage via PUT
    if (isDirectGcs && uploadUrl) {
      await axios.put(uploadUrl, file, {
        headers: {
          'Content-Type': file.type || 'application/pdf',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total && onProgress) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percent);
          }
        },
      });
    }

    // 3. Persiste a nova procuração ativa com vigência no banco
    const response = await api.post<ProcuracaoComplianceContract>('/compliance/procuracao', {
      url: finalUrl,
      nome_original: file.name,
      vigencia_meses: vigenciaMeses,
    });

    return response.data;
  }

  /**
   * Baixa qualquer documento de compliance através da API interna por stream/blob
   */
  async downloadDocumento(path: string, nomeArquivo?: string): Promise<void> {
    const response = await api.get('/compliance/download-documento', {
      params: { path },
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = nomeArquivo || 'documento.pdf';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
}

export const complianceService = new ComplianceService();
